import { z } from 'zod';
import { isoDate, uuid } from './common.js';

/** Characters of `brevo_sync_jobs.last_error` the dashboard shows. */
export const DASHBOARD_ERROR_CHARS = 200;
/** Failed Brevo jobs listed on the dashboard, latest first. */
export const DASHBOARD_FAILURES = 20;

const places = z.object({
  /** Paid registrations. */
  paid: z.number().int(),
  /** Pending registrations whose order is still inside its 20-min hold. */
  held: z.number().int(),
  /** Null = unlimited. */
  capacity: z.number().int().nullable(),
  /** capacity − paid − held, never below 0; null when unlimited. */
  remaining: z.number().int().nullable(),
});

const funnelStep = z.object({
  /** Orders created in the period (checkouts started), any status. */
  started: z.number().int(),
  /** Of those, paid at some point: status paid or refunded. */
  paid: z.number().int(),
  /** Of those, refunded (in full or in part). */
  refunded: z.number().int(),
  /** Paid orders of the period that used a voucher. */
  vouchersUsed: z.number().int(),
});
export type FunnelStep = z.infer<typeof funnelStep>;

/** GET /admin/dashboard (A-09): read-only, computed from the database only. */
export const adminDashboard = z.object({
  generatedAt: isoDate,
  formats: z.array(places.extend({ id: uuid, name: z.string() })),
  /** Event total; `capacity` / `remaining` null as soon as one format is unlimited. */
  total: places,
  revenue: z.object({
    /** Amount charged on orders ever paid (paid + refunded), insurance included. */
    grossCents: z.number().int(),
    refundedCents: z.number().int(),
    netCents: z.number().int(),
    /** Insurance line of those orders (part of gross). */
    insuranceCents: z.number().int(),
    insuredOrders: z.number().int(),
    /** Taken off by vouchers on those orders. */
    discountCents: z.number().int(),
    paidOrders: z.number().int(),
  }),
  funnel: z.object({ today: funnelStep, last7Days: funnelStep, sinceOpening: funnelStep }),
  /** Items of the A-05 review queue: certificates and licences waiting for review. */
  documentsPending: z.number().int(),
  brevo: z.object({
    failed: z.number().int(),
    /** Still retrying (status queued) for more than an hour. */
    stuck: z.number().int(),
    latestFailures: z.array(
      z.object({
        id: uuid,
        type: z.string(),
        email: z.string().nullable(),
        attempts: z.number().int(),
        /** Truncated to `DASHBOARD_ERROR_CHARS`. */
        lastError: z.string().nullable(),
        createdAt: isoDate,
      }),
    ),
  }),
});
export type AdminDashboard = z.infer<typeof adminDashboard>;
